import { Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { useAdvisoryToolsQuery } from "AdvisoryManagement/Api/Queries/useAdvisoryTools";
import { useContractsQuery } from "AdvisoryManagement/Api/Queries/useContractsQuery";
import { AddProposedSolutions } from "AdvisoryManagement/Components/Client/ProposedSolutions/AddProposedSolutions";
import { ProposedSolutionRow } from "AdvisoryManagement/Components/Client/ProposedSolutions/ProposedSolutionRow";
import type { DummyExtendedContract } from "AdvisoryManagement/types";
import { ContractStatus } from "Infrastructure/Api/Api";
import { SectionCard } from "Shared/Cards/SectionCard";
import { SortableContainer } from "Shared/DragDrop/SortableContainer";
import { SortableItem } from "Shared/DragDrop/SortableItem";

export const ProposedSolutions = () => {
	const { advisoryCaseId } = useParams<{ advisoryCaseId: string }>();

	const { data: proposedSolutions } = useContractsQuery(advisoryCaseId, [
		ContractStatus.Draft,
	]);

	const categories = useMemo(
		() => [...new Set(proposedSolutions?.items?.map((s) => s.categoryCode))],
		[proposedSolutions?.items],
	);

	const { data: advisoryTools } = useAdvisoryToolsQuery(categories);

	const [orderedSolutions, setOrderedSolutions] = useState<
		DummyExtendedContract[]
	>([]);

	const prevLengthRef = useRef<number>(0);

	useEffect(() => {
		const currentLength = proposedSolutions?.items?.length ?? 0;
		if (currentLength !== prevLengthRef.current) {
			setOrderedSolutions(proposedSolutions?.items ?? []);
			prevLengthRef.current = currentLength;
		}
	}, [proposedSolutions?.items?.length, proposedSolutions?.items]);

	return (
		<SectionCard
			title="Navrhovaná řešení"
			action={<AddProposedSolutions />}
			separateContent
		>
			{orderedSolutions.length === 0 ? (
				<Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
					Žádná navrhovaná řešení
				</Typography>
			) : (
				<SortableContainer
					items={orderedSolutions}
					getItemId={(item) => item.contractId}
					onReorder={setOrderedSolutions}
				>
					{orderedSolutions.map((solution) => (
						<SortableItem key={solution.contractId} id={solution.contractId}>
							<ProposedSolutionRow
								solution={solution}
								tools={
									advisoryTools?.find(
										(t) => t.categoryCode === solution.categoryCode,
									)?.tools
								}
							/>
						</SortableItem>
					))}
				</SortableContainer>
			)}
		</SectionCard>
	);
};
